import { useQuery } from '@tanstack/react-query';

import {
  getPredictionStateAccount,
  MAX_SOLANA_PUBKEYS_PER_CALL,
} from '@/constants/helpers';
import { globalState, program } from '@/constants/solana';
import { PredictionMarketState } from '@/types/contract';
import { compareData } from '@/utils/fetch.utils';

export const fetchMarketState = async (id?: number) => {
  if (!globalState || id === undefined) return null;
  return await program.account.predictionMarketState.fetch(
    getPredictionStateAccount(globalState, id),
  );
};

export const useFetchMarketState = (id?: number) => {
  return useQuery({
    queryKey: ['market-state', id],
    refetchInterval: 60 * 1000,
    structuralSharing: compareData,
    queryFn: async () => {
      return await fetchMarketState(id);
    },
    enabled: id !== undefined,
  });
};

export const fetchMarketStates = async (
  ids: number[],
): Promise<Record<number, PredictionMarketState | null>> => {
  if (!globalState || ids.length === 0) return {};
  const pubkeys = ids.map((id) => getPredictionStateAccount(globalState, id));

  const result: Record<number, PredictionMarketState | null> = {};
  for (let i = 0; i < pubkeys.length; i += MAX_SOLANA_PUBKEYS_PER_CALL) {
    const slice = pubkeys.slice(i, i + MAX_SOLANA_PUBKEYS_PER_CALL);
    const sliceIds = ids.slice(i, i + MAX_SOLANA_PUBKEYS_PER_CALL);
    const part =
      await program.account.predictionMarketState.fetchMultiple(slice);

    for (let j = 0; j < sliceIds.length; j++) {
      result[sliceIds[j]] = part[j] ?? null;
    }
  }
  return result;
};

export const useFetchMarketStates = (
  ids: number[],
  refetchInterval = 60 * 1000,
) => {
  return useQuery({
    queryKey: ['market-states', ids],
    refetchInterval,
    structuralSharing: compareData,
    placeholderData: (prev) => prev,
    enabled: ids.length > 0,
    queryFn: async () => {
      return await fetchMarketStates(ids);
    },
  });
};

export const useFetchMarketStatesScroll = (ids: number[]) => {
  return useQuery({
    queryKey: ['market-states-scroll'],
    refetchInterval: 2 * 1000,
    structuralSharing: compareData,
    placeholderData: (prev) => prev,

    enabled: ids.length > 0,
    queryFn: async () => {
      return await fetchMarketStates(ids);
    },
  });
};
